/**
 * Compose any request against an authorized connection.
 *
 * The declared actions cover the calls the manifests describe. This is the rest
 * of the provider's API: pick a connection, type a method and path, and the
 * backend attaches the credential and sends it. Nothing about the request is
 * baked in anywhere.
 *
 * The host is deliberately not editable — it comes from the connector's manifest
 * and is enforced server-side, so a stored credential can only ever be sent to
 * the app it belongs to.
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { requestsApi, type CustomRequest } from '@/core/api/requests.api'
import { queryClient } from '@/lib/query.client'
import { SchemaOutput } from '@/core/schema-renderer/SchemaOutput'
import {
  Callout,
  CopyButton,
  ErrorNotice,
  Loading,
  formatDuration,
} from '@/shared/components'
import { useToast } from '@/shared/components/toast'

const METHODS: CustomRequest['method'][] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

interface Pair {
  key: string
  value: string
  on: boolean
}

const EMPTY_PAIR: Pair = { key: '', value: '', on: true }

export function CustomRequestPanel({ connectionId }: { connectionId: string }) {
  const toast = useToast()

  const caps = useQuery({
    queryKey: ['capabilities', connectionId],
    queryFn: () => requestsApi.capabilities(connectionId),
    staleTime: 10 * 60_000,
  })

  const [method, setMethod] = useState<CustomRequest['method']>('GET')
  const [path, setPath] = useState('')
  const [queryPairs, setQueryPairs] = useState<Pair[]>([{ ...EMPTY_PAIR }])
  const [headerPairs, setHeaderPairs] = useState<Pair[]>([{ ...EMPTY_PAIR }])
  const [bodyText, setBodyText] = useState('')
  const [autoPaginate, setAutoPaginate] = useState(false)
  const [itemsPath, setItemsPath] = useState('')

  // Reset when the connection changes: a path valid on one app is meaningless
  // on another.
  useEffect(() => {
    setPath('')
    setQueryPairs([{ ...EMPTY_PAIR }])
    setHeaderPairs([{ ...EMPTY_PAIR }])
    setBodyText('')
    setAutoPaginate(false)
  }, [connectionId])

  const sendsBody = method !== 'GET' && method !== 'DELETE'

  const bodyError = useMemo(() => {
    if (!sendsBody || !bodyText.trim()) return null
    try {
      JSON.parse(bodyText)
      return null
    } catch (err) {
      return err instanceof Error ? err.message : 'Invalid JSON'
    }
  }, [bodyText, sendsBody])

  const send = useMutation({
    mutationFn: () => {
      const payload: CustomRequest = {
        method,
        path: path.trim(),
        query: pairsToObject(queryPairs),
        headers: pairsToObject(headerPairs) as Record<string, string>,
        body: sendsBody && bodyText.trim() ? JSON.parse(bodyText) : undefined,
        paginate: autoPaginate
          ? {
              type: 'page',
              page_param: 'page',
              size_param: 'per_page',
              page_size: 50,
              max_pages: 3,
              max_items: 150,
              items_path: itemsPath.trim() || null,
            }
          : null,
      }
      return requestsApi.send(connectionId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executions'] })
    },
    onError: (err) => toast.error(err, 'Request failed'),
  })

  if (caps.isLoading) return <Loading label="Reading what this connection can reach…" />
  if (caps.error) {
    return <ErrorNotice error={caps.error} onRetry={() => caps.refetch()} />
  }
  const c = caps.data!

  return (
    <div className="stack" style={{ gap: 14 }}>
      <Callout kind="accent" title="Any endpoint on this app">
        <p className="small">
          This connection is authorized for{' '}
          <span className="mono">{c.base_url}</span> using{' '}
          <span className="mono">{c.auth_type}</span>. Credentials are attached by
          the server — you only supply the endpoint. The host cannot be changed.
        </p>
      </Callout>

      {/* --- request line --- */}
      <div className="field">
        <label className="field-label" htmlFor="cr-path">
          Request
        </label>
        <div className="row-tight" style={{ flexWrap: 'nowrap' }}>
          <select
            className="select"
            style={{ maxWidth: 108, flex: 'none' }}
            value={method}
            onChange={(e) => setMethod(e.target.value as CustomRequest['method'])}
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <span className="mono faint truncate" style={{ flex: 'none', maxWidth: 210 }}>
            {c.base_url.replace(/^https?:\/\//, '')}
          </span>
          <input
            id="cr-path"
            className="input mono"
            placeholder="/albums/2/photos"
            value={path}
            spellCheck={false}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && path.trim() && !bodyError) send.mutate()
            }}
          />
        </div>
        <p className="field-help">
          A path, or a full URL on this same host. Press Enter to send.
        </p>
      </div>

      {c.known_paths.length > 0 && (
        <div className="field">
          <span className="field-label">Paths this connector already declares</span>
          <div className="multiselect" style={{ maxHeight: 118 }}>
            {c.known_paths.map((known) => {
              const [knownMethod, ...rest] = known.split(' ')
              const knownPath = rest.join(' ')
              // Templated action paths are starting points, not literal values.
              const templated = knownPath.includes('{{')
              return (
                <button
                  key={known}
                  type="button"
                  className="chip"
                  title={templated ? 'Replace the {{...}} part with a real value' : undefined}
                  onClick={() => {
                    setMethod(knownMethod as CustomRequest['method'])
                    setPath(knownPath.replace(/\{\{[^}]+\}\}/g, ''))
                  }}
                >
                  <span className="mono">{knownMethod}</span> {knownPath}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <PairEditor
        label="Query parameters"
        pairs={queryPairs}
        onChange={setQueryPairs}
        keyPlaceholder="per_page"
        valuePlaceholder="50"
      />

      <PairEditor
        label="Extra headers"
        pairs={headerPairs}
        onChange={setHeaderPairs}
        keyPlaceholder="X-Custom"
        valuePlaceholder="value"
        help={`Authorization is set by the server and cannot be overridden here. This connector already sends: ${
          Object.keys(c.default_headers).join(', ') || 'no extra headers'
        }.`}
      />

      {sendsBody && (
        <div className="field">
          <label className="field-label" htmlFor="cr-body">
            JSON body
          </label>
          <textarea
            id="cr-body"
            className="textarea textarea-mono"
            rows={7}
            spellCheck={false}
            placeholder={'{\n  "title": "hello"\n}'}
            value={bodyText}
            aria-invalid={bodyError ? true : undefined}
            onChange={(e) => setBodyText(e.target.value)}
          />
          {bodyError && (
            <p className="field-error">Not valid JSON yet — {bodyError}</p>
          )}
        </div>
      )}

      <div className="field">
        <label className="checkbox-row" htmlFor="cr-paginate">
          <input
            id="cr-paginate"
            type="checkbox"
            checked={autoPaginate}
            onChange={(e) => setAutoPaginate(e.target.checked)}
          />
          <span>
            <span className="field-label">Follow pages automatically</span>
            <p className="field-help">
              For collection endpoints. Walks up to 3 pages and stitches the
              records into one array.
            </p>
          </span>
        </label>
        {autoPaginate && (
          <input
            className="input mono"
            style={{ marginTop: 6 }}
            placeholder="items path, e.g. data or results — leave blank if the body is the array"
            value={itemsPath}
            onChange={(e) => setItemsPath(e.target.value)}
          />
        )}
      </div>

      <div className="row">
        <button
          className="btn btn-primary"
          disabled={send.isPending || !path.trim() || Boolean(bodyError)}
          onClick={() => send.mutate()}
        >
          {send.isPending && <span className="spinner" />}
          {send.isPending ? 'Sending…' : 'Send request'}
        </button>
        {c.docs_url && (
          <a className="btn" href={c.docs_url} target="_blank" rel="noreferrer noopener">
            API reference ↗
          </a>
        )}
        {c.granted_scopes.length > 0 && (
          <span className="small faint" title={c.granted_scopes.join(', ')}>
            {c.granted_scopes.length} scopes granted
          </span>
        )}
      </div>

      {send.error && <ErrorNotice error={send.error} />}

      {send.data && (
        <div className="card stack" style={{ gap: 10 }}>
          <div className="row">
            <span
              className={`badge ${
                (send.data.status_code ?? 0) < 300 ? 'badge-ok' : 'badge-warn'
              }`}
            >
              <span className="dot" />
              {send.data.status_code}
            </span>
            <span className="small muted">{formatDuration(send.data.duration_ms)}</span>
            {send.data.pages > 1 && (
              <span className="badge badge-accent">{send.data.pages} pages</span>
            )}
            {Array.isArray(send.data.output) && (
              <span className="small muted">{send.data.output.length} records</span>
            )}
            <div className="spacer" />
            <span className="mono small faint truncate" style={{ maxWidth: 300 }}>
              {send.data.request_preview.url}
            </span>
            <CopyButton value={send.data.request_preview.url ?? ''} label="Copy URL" />
          </div>
          {/* No output_schema exists for an ad-hoc call, so the renderer infers
              structure from the data itself. */}
          <SchemaOutput output={send.data.output} />
        </div>
      )}
    </div>
  )
}

// --- key/value editor -------------------------------------------------------

function PairEditor({
  label,
  pairs,
  onChange,
  keyPlaceholder,
  valuePlaceholder,
  help,
}: {
  label: string
  pairs: Pair[]
  onChange: (next: Pair[]) => void
  keyPlaceholder: string
  valuePlaceholder: string
  help?: string
}) {
  const update = (index: number, patch: Partial<Pair>) => {
    const next = pairs.map((p, i) => (i === index ? { ...p, ...patch } : p))
    // Keep one blank row at the end so adding another never needs a click.
    if (index === pairs.length - 1 && (patch.key || patch.value)) {
      next.push({ ...EMPTY_PAIR })
    }
    onChange(next)
  }

  const active = pairs.filter((p) => p.key.trim()).length

  return (
    <div className="field">
      <span className="field-label">
        {label}
        {active > 0 && <span className="badge badge-neutral">{active}</span>}
      </span>
      <div className="stack" style={{ gap: 5 }}>
        {pairs.map((pair, index) => (
          <div className="row-tight" key={index} style={{ flexWrap: 'nowrap' }}>
            <input
              className="input mono"
              style={{ maxWidth: 190 }}
              placeholder={keyPlaceholder}
              value={pair.key}
              onChange={(e) => update(index, { key: e.target.value })}
            />
            <input
              className="input mono"
              placeholder={valuePlaceholder}
              value={pair.value}
              onChange={(e) => update(index, { value: e.target.value })}
            />
            {pairs.length > 1 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                aria-label="Remove"
                onClick={() => onChange(pairs.filter((_, i) => i !== index))}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      {help && <p className="field-help">{help}</p>}
    </div>
  )
}

function pairsToObject(pairs: Pair[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const pair of pairs) {
    const key = pair.key.trim()
    if (key && pair.on) out[key] = pair.value
  }
  return out
}
